import { QueryKeys } from '@/consts';
import { activityRepository, userRepository } from '@/repositories';
import type { Activity } from '@/repositories';
import { useSuiClient } from '@mysten/dapp-kit';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useCurrency } from './useCurrency';
import { shortenHex, toRoundFixed } from '@/utils';
import { showNotification } from '@/lib/notifications/notification-center';
import { useActivityDetailsDialogStore } from '@/stores/activity-details-dialog-store';
import { SuiFrenPortrait } from '@/components/suifren/SuiFrenPortrait';
import { useEventSuiFrenAppearance } from '@/hooks/use-event-suifren';
import { getDeterministicSuiFren } from '@/utils/deterministicAppearance';
import { BIG_WIN_RULES, isBigWinActivity } from '@/utils/activity';
import { useResolvedSuinsName, useSuinsResolver } from './use-suins-resolution';

type ActivitySnapshot = {
	txId?: string;
	betId?: string;
	gambler?: string;
	from?: string;
	at?: number;
	reward?: number | string;
	stake?: number | string;
	coin_type?: string;
};

const DEFAULT_COIN_TYPE = '0x2::sui::SUI';
const BIG_WIN_ACCENT_RGB = '124 58 237';

const bigWinBroadcastState = {
	initialized: false,
	latestProcessedTimestamp: 0,
	seenKeys: new Set<string>(),
};

const resolveBigWinSeed = (address?: string | null) => {
	const normalized = address?.trim();
	if (!normalized) {
		return 'suigar';
	}
	return shortenHex(normalized);
};

export const BigWinSuiFren = ({ address }: { address?: string }) => {
	const { data: resolvedName } = useResolvedSuinsName(address, { enabled: Boolean(address) });

	const seed = useMemo(() => {
		if (resolvedName && typeof resolvedName === 'string' && resolvedName.trim().length > 0) {
			return resolvedName.trim();
		}
		return resolveBigWinSeed(address);
	}, [address, resolvedName]);

	const deterministic = useMemo(() => getDeterministicSuiFren(seed, { variant: 'auto' }), [seed]);
	const { attributes, accessories } = useEventSuiFrenAppearance({
		variant: deterministic.variant,
		attributes: { ...deterministic.attributes, expression: 'ourah' } as any,
	});

	return (
		<div className="relative flex h-full w-full items-end justify-center overflow-hidden rounded-xl bg-linear-to-br from-purple-900/75 via-purple-800/80 to-purple-700/75">
			<SuiFrenPortrait
				attributes={attributes}
				accessories={accessories ?? deterministic.accessories}
				size="custom"
				frame="none"
				zoom={1.4}
				className="h-full w-full"
				showShadow={false}
			/>
		</div>
	);
};

const buildActivityKey = (activity: ActivitySnapshot): string | undefined => {
	if (activity.txId) {
		return activity.txId;
	}
	if (activity.betId) {
		return activity.betId;
	}
	const fallbackParts = [activity.gambler, typeof activity.at === 'number' ? activity.at.toString() : undefined, activity.from].filter(Boolean);
	return fallbackParts.length > 0 ? fallbackParts.join('-') : undefined;
};

type UseListOfObjectsOptions = {
	enabled?: boolean;
	autoUpdate?: boolean;
};

export const useListOfObjects = ({ enabled = true, autoUpdate: autoUpdateDefault = true }: UseListOfObjectsOptions = {}) => {
	const [autoUpdate, setAutoUpdate] = useState(autoUpdateDefault);
	const client = useSuiClient();
	const { convertAmount, selectedCurrency } = useCurrency();
	const openActivityDetailsDialog = useActivityDetailsDialogStore((state) => state.openDialog);
	const { resolveName } = useSuinsResolver();
	const queryEnabled = Boolean(enabled);
	const autoUpdateEnabled = queryEnabled && autoUpdate;

	const { data: lootboxes, isLoading: lootboxesIsLoading } = useQuery({
		queryKey: [QueryKeys.Lootboxes],
		queryFn: async () => {
			return userRepository.getLootboxes(client);
		},
		enabled: queryEnabled,
	});

	const { data: coinFlipActivities, isLoading: coinFlipActivitiesLoading } = useQuery({
		queryKey: [QueryKeys.CoinflipActivities],
		queryFn: async () => {
			return activityRepository.getCoinflipActivities(client);
		},
		enabled: queryEnabled,
		refetchInterval: autoUpdateEnabled ? 2000 : false,
		refetchOnWindowFocus: true,
	});

	const { data: plinkoActivities, isLoading: plinkoActivitiesLoading } = useQuery({
		queryKey: [QueryKeys.PlinkoActivities],
		queryFn: async () => {
			return activityRepository.getPlinkoActivities(client);
		},
		enabled: queryEnabled,
		refetchInterval: autoUpdateEnabled ? 2000 : false,
		refetchOnWindowFocus: true,
	});

	const { data: lootboxActivities, isLoading: lootboxActivitiesLoading } = useQuery({
		queryKey: [QueryKeys.LootboxActivities],
		queryFn: async () => {
			return activityRepository.getLootboxActivities(client);
		},
		enabled: queryEnabled,
		refetchInterval: autoUpdateEnabled ? 2000 : false,
		refetchOnWindowFocus: true,
	});

	const { data: limboActivities, isLoading: limboActivitiesLoading } = useQuery({
		queryKey: [QueryKeys.LimboActivities],
		queryFn: async () => {
			return activityRepository.getLimboActivities(client);
		},
		enabled: queryEnabled,
		refetchInterval: autoUpdateEnabled ? 2000 : false,
		refetchOnWindowFocus: true,
	});

	const { data: diceActivities, isLoading: diceActivitiesLoading } = useQuery({
		queryKey: [QueryKeys.DiceActivities],
		queryFn: async () => {
			return activityRepository.getDiceActivities(client);
		},
		enabled: queryEnabled,
		refetchInterval: autoUpdateEnabled ? 2000 : false,
		refetchOnWindowFocus: true,
	});

	const { data: suiroyaleActivities, isLoading: suiroyaleActivitiesLoading } = useQuery({
		queryKey: [QueryKeys.SuiroyaleActivities],
		queryFn: async () => {
			// TODO: Implement when Suiroyale contract is ready
			// return activityRepository.getSuiroyaleActivities(client);
			return [];
		},
		enabled: queryEnabled,
		refetchInterval: autoUpdateEnabled ? 2000 : false,
		refetchOnWindowFocus: true,
	});

	const setAutoUpdateState = (enabled: boolean) => {
		setAutoUpdate(enabled);
	};

	useEffect(() => {
		if (!queryEnabled) {
			return;
		}
		const collections = [coinFlipActivities, plinkoActivities, limboActivities, diceActivities, lootboxActivities, suiroyaleActivities];

		const merged = collections
			.flatMap((list) => list ?? [])
			.filter((activity) => activity && typeof activity.at === 'number' && Number.isFinite(activity.at))
			.sort((a, b) => a.at - b.at);

		if (merged.length === 0) {
			return;
		}

		const state = bigWinBroadcastState;
		let nextLatestTimestamp = state.latestProcessedTimestamp;

		if (!state.initialized) {
			merged.forEach((activity) => {
				if (typeof activity.at === 'number') {
					nextLatestTimestamp = Math.max(nextLatestTimestamp, activity.at);
				}
				const key = buildActivityKey(activity);
				if (key) {
					state.seenKeys.add(key);
				}
			});

			state.latestProcessedTimestamp = nextLatestTimestamp > 0 ? nextLatestTimestamp : Date.now();
			state.initialized = true;
			return;
		}

		const symbol = selectedCurrency.symbol;

		const maybeBroadcastBigWin = async (activity: ActivitySnapshot) => {
			if (typeof activity.at === 'number') {
				nextLatestTimestamp = Math.max(nextLatestTimestamp, activity.at);
			}

			const key = buildActivityKey(activity);
			if (!key) {
				return;
			}

			if (state.seenKeys.has(key)) {
				return;
			}

			state.seenKeys.add(key);

			if (typeof activity.at === 'number' && activity.at < state.latestProcessedTimestamp) {
				return;
			}

			const reward = Number(activity.reward ?? 0);
			const stake = Number(activity.stake ?? 0);
			if (!Number.isFinite(reward) || reward <= stake) {
				return;
			}

			const coinType = typeof activity.coin_type === 'string' ? activity.coin_type : DEFAULT_COIN_TYPE;

			const isBigWin = isBigWinActivity(activity as Activity, convertAmount, BIG_WIN_RULES);
			if (!isBigWin) {
				return;
			}

			const profitConverted = convertAmount(reward - stake, coinType);
			if (!Number.isFinite(profitConverted)) {
				return;
			}

			const profitDisplay =
				profitConverted >= 100
					? Math.round(profitConverted).toLocaleString()
					: toRoundFixed(profitConverted).toLocaleString(undefined, {
							minimumFractionDigits: profitConverted >= 10 ? 1 : 2,
							maximumFractionDigits: profitConverted >= 10 ? 1 : 2,
						});

			const shortAddress = shortenHex(activity.gambler ?? '');
			let resolvedName: string | null = null;
			try {
				resolvedName = await resolveName(activity.gambler);
			} catch (error) {
				console.warn('Failed to resolve Suins name for big win toast', error);
			}
			const displayHandle = resolvedName ?? shortAddress;
			const gameLabel = activity.from ? activity.from.charAt(0).toUpperCase() + activity.from.slice(1) : 'Suigar';

			showNotification({
				intent: 'success',
				title: `Big win on ${gameLabel}!`,
				description: `${displayHandle} just won ${profitDisplay} ${symbol}`,
				position: 'top-center',
				duration: 6_000,
				className:
					'pointer-events-auto gap-3 p-3 py-2.5 border border-purple-300/70 bg-purple-950/90 text-sm leading-tight text-purple-50 shadow-[0_22px_60px_-28px_rgba(88,28,135,0.65)] backdrop-blur-md [--foreground:#f6f2ff] [--muted-foreground:rgba(232,224,255,0.9)] [--primary:#d8b4fe] [--primary-foreground:#1f0f3d] [--background:rgba(49,10,94,0.92)] [--card:rgba(49,10,94,0.92)] [--border:rgba(167,139,250,0.7)] [--muted:rgba(124,58,237,0.16)] [--ring:rgba(167,139,250,0.85)]',
				accentColor: BIG_WIN_ACCENT_RGB,
				illustration: <BigWinSuiFren address={activity.gambler} />,
				style: {
					padding: '10px 12px',
					backgroundColor: 'rgba(37, 6, 71, 0.95)',
					borderColor: 'rgba(167, 139, 250, 0.72)',
					boxShadow: '0 26px 68px -40px rgba(88, 28, 135, 0.55)',
				},
				closeButton: true,
				dedupeKey: activity.txId ? `bigwin-${activity.txId}` : undefined,
				action: {
					label: 'Details',
					onClick: () =>
						openActivityDetailsDialog({
							activity: activity as Activity,
							showGameCta: true,
						}),
				},
			});
		};

		void (async () => {
			for (const activity of merged) {
				// Sequential to keep ordering and dedupe consistent.
				// Resolver is cached, so the await is cheap when already resolved.
				await maybeBroadcastBigWin(activity);
			}

			state.latestProcessedTimestamp = Math.max(state.latestProcessedTimestamp, nextLatestTimestamp);
		})();
	}, [
		coinFlipActivities,
		plinkoActivities,
		limboActivities,
		diceActivities,
		lootboxActivities,
		suiroyaleActivities,
		convertAmount,
		selectedCurrency.symbol,
		openActivityDetailsDialog,
		resolveName,
	]);

	return {
		lootboxes,
		lootboxesIsLoading,
		lootboxActivities,
		lootboxActivitiesLoading,
		coinFlipActivities,
		coinFlipActivitiesLoading,
		plinkoActivities,
		plinkoActivitiesLoading,
		limboActivities,
		limboActivitiesLoading,
		diceActivities,
		diceActivitiesLoading,
		suiroyaleActivities,
		suiroyaleActivitiesLoading,
		setAutoUpdateState,
	};
};
